import React, { useCallback, useEffect, useState } from "react";
import Form from "../../../HelperComponents/Form/Form";
import {
  getAWSR,
  getPBAP,
  getRBT,
  getRBTC,
} from "../../../Service/CredentialingService";
import { FontSizes, FontWeights } from "@fluentui/react";

interface EditCredentialsProps {
  userProfile: any;
  staff?: any;
}

const EditCredentials = (props: EditCredentialsProps) => {
  const [RBTC, setRBTC] = useState<any>(null);
  const [RBT, setRBT] = useState<any>(null);
  const [PBAP, setPBAP] = useState<any>(null);
  const [AWSR, setAWSR] = useState<any>(null);

  const styles = {
    container: {
      fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
      padding: "12px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(1fr, 1fr))",
      gridGap: "15px",
      alignItems: "start",
    },
    card: {
      backgroundColor: "hsl(var(--background))",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
      padding: "0 20px",
    },
    sectionTitle: {
      fontSize: "16px",
      fontWeight: "600",
      color: "hsl(var(--primary))",
      paddingBottom: "8px",
      borderBottom: "1px solid hsl(var(--border))",
      marginBottom: "12px",
    },
    detailRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      marginBottom: "12px",
    },
    label: {
      fontSize: "13px",
      fontWeight: "600",
      color: "hsl(var(--foreground))",
    },
    value: {
      fontSize: "13px",
      color: "hsl(var(--foreground))",
      textAlign: "left" as const,
    },
    active: {
      color: "hsl(var(--success))",
      fontWeight: "500",
    },
    inactive: {
      color: "hsl(var(--error))",
      fontWeight: "500",
    },
  };

  const CredentialingInfoFields = [
    {
      id: "highestDegree",
      title: "Highest Degree",
      type: "List",
      listName: "Degree",
      dropdownText: ["name"],
      visible: true,
      value: props.userProfile?.HighestDegreeType,
      destinations: [
        { destination: "userProfile", fieldName: "HighestDegreeType" },
      ],
    },
    {
      id: "certification#",
      title: "BACB Certification #",
      type: "Text",
      visible: true,
      value: props.userProfile?.CertificationNumber,
      destinations: [
        { destination: "userProfile", fieldName: "CertificationNumber" },
      ],
    },
    {
      id: "analystLevel",
      title: "Analyst Level",
      type: "List",
      options: [
        "BCaBA",
        "BCBA",
        "BCBA-D",
        "Behavior Assistant",
        "Licensed Professional",
        "Program Manager",
        "RBT",
      ],
      value: props.userProfile?.AnalystLevel,
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "AnalystLevel" }],
    },
    {
      id: "applicantBACBID",
      title: "BACB ID",
      type: "Text",
      description:
        "Please enter a valid BACB ID in the format BACBXXXXXX (ex. BACB123456)",
      visible: true,
      value: props.userProfile?.ApplicantBACBID,
      destinations: [
        { destination: "userProfile", fieldName: "ApplicantBACBID" },
      ],
    },
    // {
    //   id: "countiesOfService",
    //   title: "Counties of Service",
    //   type: "PrioritizedList",
    //   multi: true,
    //   listName: "County",
    //   dropdownText: ["County"],
    //   instructionText:
    //     "Select your primary county of service first, followed by any additional counties",
    //   listTagText: "Primary",
    //   value: props.userProfile?.County,
    //   visible: true,
    //   destinations: [{ destination: "userProfile", fieldName: "County" }],
    // },
    {
      id: "caqh#",
      title: "CAQH #",
      value: props.userProfile?.CAQHNumber,
      type: "Text",
      description:
        "CAQH- The CAQH is a number that is assigned to all providers to enhance and expedite the credentialing process. It is a complete profile that the insurance companies can pull from to verify information on the provider.",
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "CAQHNumber" },
        { destination: "DFC", fieldName: "CAQH" },
      ],
    },
    {
      id: "caqhCompletion",
      title: "CAQH Completion/Last Attestation Date",
      value: props.userProfile?.CAQHReattest,
      type: "Date",
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "CAQHReattest" },
        { destination: "DFC", fieldName: "CAQH_x0020_Reattest" },
      ],
    },
    {
      id: "npi",
      title: "NPI #",
      value: props.userProfile?.NPI || "",
      required: true,
      type: "Text",
      description:
        "The NPI stands for National Provider Identifier and is a standard unique health identifier for health care providers, including BCBA’s, BCaBA’s, and technicians (behavior assistants). NPI was adopted by Secretary of Health and Human Services (HHS) as mandated by the Health Insurance Portability and Accountability Act of 1996 (HIPAA).",
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "NPI" },
        { destination: "DFC", fieldName: "NPI" },
      ],
    },
    {
      id: "npiEnumerationDate",
      title: "NPI Enumeration Date",
      value: props.userProfile?.NPIEnumerationDate,
      type: "Date",
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "NPIEnumerationDate" },
      ],
    },
    {
      id: "npiTaxonomy",
      title: "NPI Taxonomy",
      value: props.userProfile?.NPITaxonomy,
      type: "Text",
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "NPITaxonomy" }],
    },
    {
      id: "previouslyredentialed",
      title: "Previously Credentialed(If Yes List Insurance Companies)",
      value: props.userProfile?.PreviouslyCredentialed,
      type: "Checkbox",
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "PreviouslyCredentialed" },
      ],
    },
    {
      id: "insuranceCompanies",
      title: "Insurance Companies",
      value: props.userProfile?.InsuranceCompanies,
      type: "List",
      listName: "InsuranceCompanies",
      dropdownText: ["name"],
      visible: true,
      multi: true,
      destinations: [
        { destination: "userProfile", fieldName: "InsuranceCompanies" },
      ],
    },
    {
      id: "forSupervisorslbl",
      title: "For Supervisors",
      type: "Label",
      visible: true,
      style: {
        color: "hsl(var(--primary))",
        fontSize: FontSizes.size16,
        fontWeight: FontWeights.bold,
        marginBottom: "20px",
      },
    },
    {
      id: "forSupervisors",
      title:
        "You must check <strong>ALL</strong> boxes and fill <strong>ALL</strong> dates to be listed as <strong>Supervisor</strong>",
      type: "Label",
      visible: true,
      style: {
        color: "red",
        marginBottom: "20px",
        fontWeight: FontWeights.regular,
      },
    },
    {
      id: "is_x0020_8_x0020_hour_x0020_cour",
      title: "8 hours supervisor course completed",
      type: "Checkbox",
      value: AWSR?.is_x0020_8_x0020_hour_x0020_cour,
      visible: true,
      destinations: [
        { destination: "AWSR", fieldName: "is_x0020_8_x0020_hour_x0020_cour" },
      ],
    },
    {
      id: "_x0038__x0020_hour_x0020_course",
      title: "Date Completed 8 hour supervisor course",
      type: "Date",
      value: AWSR?._x0038__x0020_hour_x0020_course,
      visible: true,
      destinations: [
        {
          destination: "AWSR",
          fieldName: "_x0038__x0020_hour_x0020_course",
        },
      ],
    },
    {
      id: "is_x0020_online_x0020_module",
      title: "Online Modules",
      type: "Checkbox",
      value: AWSR?.is_x0020_online_x0020_module,
      visible: true,
      destinations: [
        { destination: "AWSR", fieldName: "is_x0020_online_x0020_module" },
      ],
    },
    {
      id: "On_x002d_line_x0020_module",
      title: "Date Completed Online Module",
      type: "Date",
      value: AWSR?.On_x002d_line_x0020_module,
      visible: true,
      destinations: [
        { destination: "AWSR", fieldName: "On_x002d_line_x0020_module" },
      ],
    },
    {
      id: "forSupervisorslbl",
      title: "Continuing Education",
      type: "Label",
      visible: true,
      style: {
        color: "hsl(var(--primary))",
        fontSize: FontSizes.size16,
        fontWeight: FontWeights.bold,
        marginBottom: "20px",
      },
    },
    {
      id: "RBTC",
      title: "Registered Behavior Technician Candidate",
      type: "Checkbox",
      visible: true,
      value: props.userProfile?.PBSRBTC,
      destinations: [{ destination: "userProfile", fieldName: "PBSRBTC" }],
    },
    {
      id: "RBTClbl",
      title: "RBT Candidate Info",
      type: "Label",
      visible: true,
      visibilityConditions: [
        {
          fieldId: "RBTC",
          operator: "checked",
        },
      ],
    },
    {
      id: "beginingDate",
      title: "Begining Date",
      type: "Date",
      value: RBTC?.Begining_x0020_Date,
      visible: true,
      visibilityConditions: [
        {
          fieldId: "RBTC",
          operator: "checked",
        },
      ],
      destinations: [{ destination: "RBTC", fieldName: "Begining_x0020_Date" }],
    },
    {
      id: "RBTCsupervisor",
      title: "Select Supervisor",
      type: "PeoplePicker",
      visible: true,
      multi: false,
      fieldMappings: {
        nameField: "RBTCSupervisor",
        emailField: "RBTCSupervisorEmail",
      },
      visibilityConditions: [
        {
          fieldId: "RBTC",
          operator: "checked",
        },
      ],
    },
    {
      id: "RBTCSupervisor",
      title: "Supervisor Name",
      type: "Text",
      value: RBTC?.Supervisor,
      visible: true,
      readOnly: true,
      visibilityConditions: [
        {
          fieldId: "RBTC",
          operator: "checked",
        },
      ],
      destinations: [{ destination: "RBTC", fieldName: "Supervisor" }],
    },
    {
      id: "RBTCSupervisorEmail",
      title: "Supervisor Email",
      type: "Text",
      value: RBTC?.Supervisors_x0020_Email,
      visible: true,
      readOnly: true,
      visibilityConditions: [
        {
          fieldId: "RBTC",
          operator: "checked",
        },
      ],
      destinations: [
        { destination: "RBTC", fieldName: "Supervisors_x0020_Email" },
      ],
    },
    {
      id: "RBT",
      title: "Registered Behavior Technician",
      type: "Checkbox",
      visible: true,
      value: props.userProfile?.PBSRBT,
      destinations: [{ destination: "userProfile", fieldName: "PBSRBT" }],
    },
    {
      id: "RBTlbl",
      title: "RBT Info",
      type: "Label",
      visible: true,
      visibilityConditions: [
        {
          fieldId: "RBT",
          operator: "checked",
        },
      ],
    },
    {
      id: "rbtCertificationDate",
      title: "Original Certification Date",
      type: "Date",
      value: RBT?.Original_x0020_Certification_x00,
      visible: true,
      visibilityConditions: [
        {
          fieldId: "RBT",
          operator: "checked",
        },
      ],
      destinations: [
        { destination: "RBT", fieldName: "Original_x0020_Certification_x00" },
      ],
    },
    {
      id: "lastCompetencyDate",
      title: "Last Competency Based Assessment",
      type: "Date",
      value: RBT?.Last_x0020_Competency_x0020_Asse,
      visible: true,
      visibilityConditions: [
        {
          fieldId: "RBT",
          operator: "checked",
        },
      ],
      destinations: [
        { destination: "RBT", fieldName: "Last_x0020_Competency_x0020_Asse" },
      ],
    },
    {
      id: "RBTsupervisor",
      title: "Select Supervisor",
      type: "PeoplePicker",
      visible: true,
      multi: false,
      fieldMappings: {
        nameField: "RBTSupervisor",
        emailField: "RBTSupervisorEmail",
      },
      visibilityConditions: [
        {
          fieldId: "RBT",
          operator: "checked",
        },
      ],
    },
    {
      id: "RBTSupervisor",
      title: "Supervisor Name",
      type: "Text",
      value: RBT?.Supervisors,
      visible: true,
      readOnly: true,
      visibilityConditions: [
        {
          fieldId: "RBT",
          operator: "checked",
        },
      ],
      destinations: [{ destination: "RBT", fieldName: "Supervisors" }],
    },
    {
      id: "RBTSupervisorEmail",
      title: "Supervisor Email",
      type: "Text",
      value: RBT?.Supervisors_x0020_Email,
      visible: true,
      readOnly: true,
      visibilityConditions: [
        {
          fieldId: "RBT",
          operator: "checked",
        },
      ],
      destinations: [
        { destination: "RBT", fieldName: "Supervisors_x0020_Email" },
      ],
    },
    {
      id: "enrolledInCollege",
      title: "Currently Enrolled in College",
      type: "Checkbox",
      visible: true,
      value: props.userProfile?.["PBS-CEC"],
      destinations: [{ destination: "userProfile", fieldName: "PBS-CEC" }],
    },
    {
      id: "enrolledBCBAorBCaBA",
      title:
        "Enrolled in University Coursework towards BCBA or BCaBA Certification",
      type: "Checkbox",
      visible: true,
      value: props.userProfile?.["PBS-EABAP"],
      destinations: [{ destination: "userProfile", fieldName: "PBS-EABAP" }],
    },
    {
      id: "enrolledBCBAorBCaBAlbl",
      title: "Behavior Analysis Program Entrollment Details",
      type: "Label",
      visible: true,
      visibilityConditions: [
        {
          fieldId: "enrolledBCBAorBCaBA",
          operator: "checked",
        },
      ],
    },
    {
      id: "Universities",
      title: "Name of University Program",
      type: "List",
      listName: "Universities",
      dropdownText: ["Title"],
      value: PBAP?.Name_x0020_of_x0020_University,
      visible: true,
      visibilityConditions: [
        {
          fieldId: "enrolledBCBAorBCaBA",
          operator: "checked",
        },
      ],
      multi: false,
      destinations: [
        { destination: "PBAP", fieldName: "Name_x0020_of_x0020_University" },
      ],
    },
    {
      id: "Type_x0020_of_x0020_Program",
      title: "Type of Program",
      type: "List",
      options: ["BCBA", "BCaBA"],
      value: PBAP?.Type_x0020_of_x0020_Program,
      visible: true,
      visibilityConditions: [
        {
          fieldId: "enrolledBCBAorBCaBA",
          operator: "checked",
        },
      ],
      destinations: [
        { destination: "PBAP", fieldName: "Type_x0020_of_x0020_Program" },
      ],
    },
    {
      id: "Expected_x0020_Graduation_x0020_",
      title: "Expected graduation date",
      type: "Date",
      visible: true,
      value: PBAP?.Expected_x0020_Graduation_x0020_,
      visibilityConditions: [
        {
          fieldId: "enrolledBCBAorBCaBA",
          operator: "checked",
        },
      ],
      destinations: [
        { destination: "PBAP", fieldName: "Expected_x0020_Graduation_x0020_" },
      ],
    },
    {
      id: "Expected_x0020_BCBA_x0020_Exam_x",
      title: "Expected BACB exam date",
      type: "Date",
      visible: true,
      value: PBAP?.Expected_x0020_BCBA_x0020_Exam_x,
      visibilityConditions: [
        {
          fieldId: "enrolledBCBAorBCaBA",
          operator: "checked",
        },
      ],
      destinations: [
        { destination: "PBAP", fieldName: "Expected_x0020_BCBA_x0020_Exam_x" },
      ],
    },
    {
      id: "BCBA_x0020_Supervisors",
      title: "Select BCBA supervisors",
      type: "PeoplePicker",
      visible: true,
      multi: true,
      value: PBAP?.BCBA_x0020_Supervisors
        ? PBAP?.BCBA_x0020_Supervisors.map((s: any) =>
            s.Email ? s.Email : null,
          ) || []
        : [],
      visibilityConditions: [
        {
          fieldId: "enrolledBCBAorBCaBA",
          operator: "checked",
        },
      ],
      destinations: [
        { destination: "PBAP", fieldName: "BCBA_x0020_SupervisorsId" },
      ],
    },
  ];

  const userFullName = props.staff?._x0070_rb1;

  const fetchContinuingEducation = useCallback(() => {
    try {
      const fetchRBTC = async () => {
        const response = await getRBTC(userFullName);
        setRBTC(response.data.items[0]);
      };
      fetchRBTC();
      const fetchRBT = async () => {
        const response = await getRBT(userFullName);
        setRBT(response.data.items[0]);
      };
      fetchRBT();
      const fetchPBAP = async () => {
        const response = await getPBAP(userFullName);
        setPBAP(response.data.items[0]);
      };
      fetchPBAP();
      const fetchAWSR = async () => {
        const response = await getAWSR(userFullName);
        setAWSR(response.data.items[0]);
      };
      fetchAWSR();
    } catch (err) {}
  }, [userFullName]);

  useEffect(() => {
    fetchContinuingEducation();
  }, [fetchContinuingEducation]);

  return (
    <div>
      <div className="contentContainer">
        <div key={1} className="content">
          <h2 className="title">Edit Credentials</h2>
          <div className="contentCard">
            <div style={styles.container}>
              <div style={styles.grid}>
                <div style={styles.card}>
                  <h3 style={styles.sectionTitle}>Credentialing Information</h3>
                  <Form
                    fields={CredentialingInfoFields}
                    columns={2}
                    mode="update"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCredentials;
